import {
  setSmoother,
  gsap,
  ScrollSmoother,
  ScrollTrigger,
  DrawSVGPlugin,
  forceScrollSmootherUpdate,
  smoother,
  scrollPosition,
} from "./gsap";
import { documentReady, observeElementResize, scrollToEl } from "./utils";
import Dom from "../utility/dom";
import { InitializeFooterAnimation } from "./animations/components/footer/footerAnimations";
import { InitializeCalloutScroll } from "./animations/blocks/callout/calloutScrollTrigger";

const body = Dom.body;
let scrollTop = smoother ? smoother.scrollTop() : 0;
window.addEventListener("resize", () => {
  scrollTop = smoother ? smoother.scrollTop() : 0;
});

const InitializeSmoothing = () => {
  gsap.registerPlugin(ScrollTrigger, DrawSVGPlugin);

  if (!!body.querySelector(".scroll-smoother-wrapper")) {
    gsap.registerPlugin(ScrollSmoother);

    ScrollTrigger.config({
      ignoreMobileResize: true,
      refreshPriority: 9,
    });

    documentReady(() => {
      observeElementResize(document.querySelector("#page"), () => {
        forceScrollSmootherUpdate();
      });

      // If the page lands on a url with an anchor, update smooth scroll to
      // that position
      const hash = window.location.hash;
      const target = hash ? document.querySelector(hash) : null;
      if (!!target) {
        setTimeout(() => {
          if (smoother) {
            ScrollSmoother.get().scrollTo(target, true, "top 50px");
          } else {
            scrollToEl(target);
          }
        }, 1000);
      }
    });

    //no scrollsmoother on mobile - too glitchy on ios
    if (!ScrollTrigger.isTouch) {
      body.classList.add("smoothscroll");

      const smoothVal = Dom.prefersMotion ? 0.7 : 0.3;

      setSmoother(
        ScrollSmoother.create({
          wrapper: ".scroll-smoother-wrapper",
          content: ".site",
          smooth: smoothVal,
          ease: "power4.out",
          effects: true,
          ignoreMobileResize: true,
          normalizeScroll: true,
          smoothTouch: 0.1,
        })
      );
    }
  }
};

const InitializeSmoothAnchorLinks = () => {
  document.querySelectorAll("a[href^='#']").forEach((a) => {
    const linkHandler = (e) => {
      e.preventDefault();
      if (a.hash) {
        if ("preventDefault" in a.dataset) return;

        const target = document.querySelector(a.hash);
        if (!!target && !Dom.prefersReducedMotion) {
          const targetPos = smoother
            ? smoother.offset(target, "top top")
            : target.getBoundingClientRect().top;

          if (smoother) {
            smoother.scrollTo(target, true, "top 50px");
          } else {
            scrollToEl(target);
          }

          // Hide the header for better landing.
          const header = document.querySelector("#masthead");
          header.classList.add("hide-while-scrolling");
          setTimeout(() => {
            header.classList.add("hide-header");
            header.classList.remove("hide-while-scrolling");
          }, 1100);
        }
      }
    };

    a.addEventListener("click", linkHandler);
  });
};

const IntializeHeaderState = () => {
  const header = document.querySelector("#masthead");

  ScrollTrigger.create({
    trigger: body,
    start: "top top",
    end: "bottom bottom",
    invalidateOnRefresh: true,
    onUpdate: (self) => {
      currentScroll = smoother ? smoother.scrollTop() : window.scrollY || window.scrollTop || document.getElementsByTagName("html")[0].scrollTop;
      const hiddenClass = "hide-header";
      const scrollClass = "scrolled-header";

      // Always show header above 400px scroll position
      if (currentScroll < 400) {
        if (header.classList.contains(scrollClass)) {
          header.classList.remove(scrollClass);
          body.classList.remove(scrollClass);
        }
        if (header.classList.contains(hiddenClass)) {
          header.classList.remove(hiddenClass);
          body.classList.remove(hiddenClass);
        }
      } else if (currentScroll >= 400) {
        if (!header.classList.contains(scrollClass)) {
          header.classList.add(scrollClass);
          body.classList.add(scrollClass);
        }

        if (self.direction == -1 && header.classList.contains(hiddenClass)) {
          header.classList.remove(hiddenClass);
          body.classList.remove(hiddenClass);
        } else if (self.direction == 1 && !header.classList.contains(hiddenClass)) {
          header.classList.add(hiddenClass);
          body.classList.add(hiddenClass);
        }
      }
    },
  });
};


const InitStickyPageSectionHeadings = () => {
  const stickySections = document.querySelectorAll('.page-section.sticky_heading');
  if(stickySections){
    const adminBar = document.querySelector('#wpadminbar');

    stickySections.forEach((section, index) => {
      const heading = section.querySelector('.eyebrow');

      ScrollTrigger.create({
        trigger: heading,
        start: () => {
          const adminBarHeight = adminBar ? adminBar.getBoundingClientRect().height : 0;
          return "top+=1 top+=" + Math.round(adminBarHeight);
        },
        end: () => {
          const sectionData = section.getBoundingClientRect();
          const headingData = heading.getBoundingClientRect();
          const paddingTop = Math.round(headingData.top - sectionData.top);
          return "+=" + Math.round(sectionData.height - headingData.height - paddingTop - 1); 
        },
        pin: true,
        pinSpacing: false,
        invalidateOnRefresh: true,
      });
    });
  }
}


const IntializeParallaxBackgrounds = () => {
  if (Dom.prefersMotion) {
    const parallaxBgEls = document.querySelectorAll(".parallax_type_bg");

    if (parallaxBgEls) {
      // Used to ensure the image is the same height / width as it's container
      // const setParallaxParentAspectRatio = (parent, el) => {
      //   const parentData = parent.getBoundingClientRect();
      //   const aspectRatio = parseInt(parentData.width) + "/" + parseInt(parentData.height);
      //   el.style["aspect-ratio"] = aspectRatio;
      // };

      parallaxBgEls.forEach((el, index) => {
        const parent = el.closest(".has_parallax, figure");

        if (parent) {
          if (!parent.classList.contains("has_parallax")) {
            parent.classList.add("has_parallax");
          }

          let speed = el.getAttribute("data-parallax-speed"); 
          
          // Check if this is an image pair. If   
          // so, change the small image speed.
          const imagePair = el.closest('.image_pair');
          if(imagePair){
            const offset = 0.19;
            if(parent.classList.contains('small-image')){
              speed = parseFloat(speed) + offset;
            }
            else {
              speed = parseFloat(speed) > (offset + 0.1) ? parseFloat(speed) - offset : 0.1;
            }
          }

          // Increase the image height to allow for  
          // vertical parallax movement, based on speed
          el.style.height = ((1 + parseFloat(speed)) * 100) + '%'; 

          let parallax = gsap.timeline({
            scrollTrigger: {
              trigger: parent,
              scrub: true,
              invalidateOnRefresh: true,
              start: "top bottom",
              end: "bottom top"
            },
          });

          parallax.fromTo(
            el,
            {
              y: 0,
            },
            {
              y: ()=>{
                const parentHeight = parent.getBoundingClientRect().height;
                const imgHeight = el.getBoundingClientRect().height;

                return ((imgHeight - parentHeight) * -1);
              },
            }
          );
        }
      });
    }
  }
};

const IntializeScalingBackgrounds = () => {
  if (Dom.prefersMotion) {
    const scalingEls = document.querySelectorAll(".is_scale_animated");

    if (scalingEls) {
      scalingEls.forEach((el) => {
        const parent = el.closest("figure");

        if (parent) {
          if (!parent.classList.contains("has_scaling")) {
            parent.classList.add("has_scaling");
          }

          let scale = el.getAttribute("data-scale-limit");
          let scaleDirection = el.classList.contains("zoom_in") ? "zoom-in" : "zoom-out";

          let scaleAnim = gsap.timeline({
            scrollTrigger: {
              trigger: parent,
              scrub: true,
              invalidateOnRefresh: true,
              start: "top bottom",
              end: "bottom top",
            },
          });

          const scaleFrom = scaleDirection == "zoom-in" ? 1 : scale;
          const scaleTo = scaleDirection == "zoom-in" ? scale : 1;

          scaleAnim.fromTo(
            el,
            {
              scale: scaleFrom,
            },
            {
              scale: scaleTo,
            }
          );
        }
      });
    }
  }
};

const InitializeMediaReveals = () => {
  if (Dom.prefersMotion) {
    const mediaEls = document.querySelectorAll(".media_reveal_animation");

    if (mediaEls) {
      mediaEls.forEach((el) => {
        if(!el.mediaRevealInitialized){
          el.mediaRevealInitialized = true;

          ScrollTrigger.create({
            trigger: el,
            start: "center bottom",
            end: "center top",
            invalidateOnRefresh: true,
            onEnter : () => {
              if(!el.classList.contains('animated')){
                el.classList.add('animated');
              }
            },
            onEnterBack : () => {
              if(!el.classList.contains('animated')){
                el.classList.add('animated');
              }
            }
          });
        }
      });
    }
  }
}

const IntializeBGLineAnimations = () => {
  if (Dom.prefersMotion) {
    const lineEls = document.querySelectorAll(".stroke_animation");

    if (lineEls) {
      lineEls.forEach((el) => {
        const style = el.getAttribute("data-style");
        const svg = el.querySelector('svg');
        const lines = el.querySelectorAll("path,circle");

        // Determine if this should scrub or play on enter
        const animType = el.getAttribute('data-animation-type');
        const scrub = animType == 'scrub';

        // Set scroll trigger on container
        let lineAnim = gsap.timeline({
          scrollTrigger: {
            trigger: el,
            scrub: scrub,
            invalidateOnRefresh: true,
            start : () => {
              const elData = el.getBoundingClientRect();
              const svgData = svg.getBoundingClientRect();
              const dif = elData.top < svgData.top ? svgData.top - elData.top : 0;
              return "top+=" + dif +" bottom";
            },
            end: () => {
              const elData = el.getBoundingClientRect();
              const svgData = svg.getBoundingClientRect();
              const dif = elData.top < svgData.top ? svgData.top - elData.top : 0;
              return "top+=" + dif + " top";
            },
            onUpdate: (self) => {
              el.style.setProperty("--progress", self.progress.toFixed(2));
            },
          },
        });

        lines.forEach((line, i) => {
          gsap.set(line, {drawSVG: 0});

          const delay = i * 0.15;
          // Add line animations
          lineAnim.to(
            line,
            {
              drawSVG: "100%",
              duration: 2,
            },
            delay
          );
        });
      });
    }
  }
};


const InitializeIconListAnimations = () => {
  const iconLists = document.querySelectorAll('.icon_list__item svg');

  if(iconLists){
    iconLists.forEach(icon => {
      ScrollTrigger.create({
        trigger: icon,
        start: "center bottom",
        end: "center top",
        invalidateOnRefresh: true,
        onEnter: () => {
          if(!icon.classList.contains('active')){
            icon.classList.add('active');
          }
        },
        onEnterBack: () => {
          if(!icon.classList.contains('active')){
            icon.classList.add('active');
          }
        },
      });
    });
  }
}

const InitializeGSAPOnPageLoad = () => {
  // add prefers reduce motion class to body if needed
  if (Dom.prefersReducedMotion) {
    body.classList.add("prefers-reduced-motion");
  }

  // InitializeFooterAnimation();
  InitializeSmoothing();
  InitializeSmoothAnchorLinks();
  IntializeHeaderState();
  IntializeScalingBackgrounds();
  InitializeCalloutScroll();
  forceScrollSmootherUpdate();
  InitializeMediaReveals();
  InitStickyPageSectionHeadings();
  InitializeIconListAnimations();

  window.addEventListener("load", (e) => {
    // Anchor links don't smooth scroll until the page has
    // been scrolled so we'll move the page 1px on load.
    const currentScroll = document.documentElement.scrollTop || document.body.scrollTop;
    scroll({
      top: currentScroll + 1,
      behavior: "smooth",
    });

    if (window.location.hash && smoother) {
      ScrollSmoother.get().scrollTo(window.location.hash, true);
    }
  });
};

export { gsap, InitializeGSAPOnPageLoad, IntializeParallaxBackgrounds, IntializeBGLineAnimations, InitializeMediaReveals };
